import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  BellIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  UserPlusIcon,
  SparklesIcon,
  GiftIcon,
  CheckIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid'

interface Notification {
  id: number
  type: 'like' | 'match' | 'message' | 'follow' | 'comment' | 'gift' | 'system'
  user?: {
    name: string
    avatar?: string
  }
  message: string
  time: string
  read: boolean
  action?: string
}

const NotificationsPage: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'unread'>('all')
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: 1,
      type: 'match',
      user: { name: 'Sarah Miller' },
      message: 'You have a new match!',
      time: '2 minutes ago',
      read: false,
      action: 'Say Hi'
    },
    {
      id: 2,
      type: 'like',
      user: { name: 'Mike Johnson' },
      message: 'liked your story "Summer Adventures"',
      time: '15 minutes ago',
      read: false
    },
    {
      id: 3,
      type: 'message',
      user: { name: 'Emma Davis' },
      message: 'sent you a message',
      time: '1 hour ago',
      read: false,
      action: 'Reply'
    },
    {
      id: 4,
      type: 'comment',
      user: { name: 'Alex Chen' },
      message: 'commented on your story',
      time: '2 hours ago',
      read: true
    },
    {
      id: 5,
      type: 'follow',
      user: { name: 'Jessica Lee' },
      message: 'started following you',
      time: '3 hours ago',
      read: true,
      action: 'Follow Back'
    },
    {
      id: 6,
      type: 'gift',
      user: { name: 'David Wilson' },
      message: 'sent you a gift',
      time: '5 hours ago',
      read: true,
      action: 'View Gift'
    },
    {
      id: 7,
      type: 'system',
      message: 'Your Premium subscription will renew in 3 days',
      time: '1 day ago',
      read: true
    },
    {
      id: 8,
      type: 'like',
      user: { name: 'Sophie Turner' },
      message: 'and 12 others liked your photo',
      time: '1 day ago',
      read: true
    }
  ])

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })))
  }

  const deleteNotification = (id: number) => {
    setNotifications(notifications.filter(n => n.id !== id))
  }

  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.read)
    : notifications

  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <BellIcon className="h-10 w-10 text-primary-400" />
            <h1 className="text-4xl font-display font-bold">Notifications</h1>
            {unreadCount > 0 && (
              <span className="px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <p className="text-white/70">Stay updated with your latest activities</p>
        </div>

        {unreadCount > 0 && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={markAllAsRead}
            className="btn-glass flex items-center space-x-2"
          >
            <CheckIcon className="h-5 w-5" />
            <span>Mark all as read</span>
          </motion.button>
        )}
      </motion.div>

      {/* Filter Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex space-x-2"
      >
        <button
          onClick={() => setFilter('all')}
          className={`px-6 py-3 rounded-xl font-medium transition-all ${
            filter === 'all'
              ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
              : 'bg-white/5 text-white/70 hover:bg-white/10'
          }`}
        >
          All ({notifications.length})
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-6 py-3 rounded-xl font-medium transition-all ${
            filter === 'unread'
              ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
              : 'bg-white/5 text-white/70 hover:bg-white/10'
          }`}
        >
          Unread ({unreadCount})
        </button>
      </motion.div>

      {/* Notifications List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-3"
      >
        {filteredNotifications.length === 0 ? (
          <div className="card-glass p-12 text-center">
            <BellIcon className="h-16 w-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">All caught up!</h3>
            <p className="text-white/70">You don't have any {filter === 'unread' ? 'unread' : ''} notifications</p>
          </div>
        ) : (
          filteredNotifications.map((notification, index) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              index={index}
              onDelete={deleteNotification}
            />
          ))
        )}
      </motion.div>
    </div>
  )
}

interface NotificationCardProps {
  notification: Notification
  index: number
  onDelete: (id: number) => void
}

const NotificationCard: React.FC<NotificationCardProps> = ({ notification, index, onDelete }) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'like':
        return <HeartSolid className="h-6 w-6 text-red-500" />
      case 'match':
        return <HeartIcon className="h-6 w-6 text-primary-400" />
      case 'message':
        return <ChatBubbleLeftIcon className="h-6 w-6 text-blue-400" />
      case 'follow':
        return <UserPlusIcon className="h-6 w-6 text-green-400" />
      case 'comment':
        return <ChatBubbleLeftIcon className="h-6 w-6 text-purple-400" />
      case 'gift':
        return <GiftIcon className="h-6 w-6 text-accent-400" />
      case 'system':
        return <SparklesIcon className="h-6 w-6 text-yellow-400" />
      default:
        return <BellIcon className="h-6 w-6 text-white/50" />
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ scale: 1.01, x: 4 }}
      className={`card-glass p-4 flex items-center space-x-4 cursor-pointer group ${
        !notification.read ? 'border-l-4 border-primary-500' : ''
      }`}
    >
      {/* Avatar/Icon */}
      <div className="flex-shrink-0">
        {notification.user ? (
          <div className="relative">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 flex items-center justify-center">
              <span className="text-lg font-bold">{notification.user.name[0]}</span>
            </div>
            <div className="absolute -bottom-1 -right-1 bg-dark-100 rounded-full p-1">
              {getIcon()}
            </div>
          </div>
        ) : (
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
            {getIcon()}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`${!notification.read ? 'font-bold' : 'font-medium'}`}>
          {notification.user && (
            <span className="text-primary-400">{notification.user.name} </span>
          )}
          {notification.message}
        </p>
        <p className="text-sm text-white/50 mt-1">{notification.time}</p>
      </div>

      {/* Action Button */}
      {notification.action && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-primary px-4 py-2 text-sm"
        >
          {notification.action}
        </motion.button>
      )}

      {/* Delete Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={(e) => {
          e.stopPropagation()
          onDelete(notification.id)
        }}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-red-500/20 rounded-lg"
      >
        <TrashIcon className="h-5 w-5 text-red-400" />
      </motion.button>

      {/* Unread Indicator */}
      {!notification.read && (
        <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full" />
      )}
    </motion.div>
  )
}

export default NotificationsPage
